import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { InvoiceService } from './invoice.service'
import {
  AddInvoice,
  AddInvoiceComplete,
  ArchiveInvoice,
  ArchiveInvoiceComplete,
  DeleteInvoice,
  DeleteInvoiceComplete,
  DisableInvoiceOnlinePayment,
  DisableInvoiceOnlinePaymentComplete,
  EnableInvoiceOnlinePayment,
  EnableInvoiceOnlinePaymentComplete,
  FetchInvoices,
  FetchInvoicesComplete,
  LoadInvoice,
  LoadInvoiceComplete,
  LoadInvoicesTotal,
  LoadInvoicesTotalComplete,
  PayInvoiceWithSavedCard,
  PayInvoiceWithSavedCardComplete,
  RefundInvoice,
  RefundInvoiceComplete,
  ScheduleInvoice,
  ScheduleInvoiceComplete,
  SearchElasticInvoicesComplete,
  SetInvoicePaymentGateway,
  SetInvoicePaymentGatewayComplete,
  UnArchiveInvoice,
  UnArchiveInvoiceComplete,
  UpdateInvoice,
  UpdateInvoiceAddress,
  UpdateInvoiceAddressComplete,
  UpdateInvoiceComplete,
  UpdateInvoiceStatus,
  UpdateInvoiceStatusComplete,
} from './invoice.actions'
import { Invoice } from './invoice.model'
import { catchError, concatMap, distinctUntilChanged, filter, map, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { isSomething, Toaster } from '@tokeet/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'
import { ActionFailed, ActionSkipped } from '@tokeet-frontend/utility'
import * as lodash from 'lodash'
import { selectAllInvoices } from './invoice.selectors'

@Injectable()
export class InvoiceEffects {
  @Effect()
  fetchElasticInvoices$ = this.actions$.pipe(
    ofType(SearchElasticInvoicesComplete),
    concatMap(({ invoices }) => {
      if (R.isEmpty(R.reject((i: Invoice) => !isSomething(i), invoices))) {
        return of(ActionSkipped())
      } else {
        return of(FetchInvoices({ ids: R.map((i) => i.id, invoices) }))
      }
    }),
    catchError((error) => of(ActionFailed({ error })))
  )

  @Effect()
  fetchInvoices$ = this.actions$.pipe(
    ofType(FetchInvoices),
    concatMap(({ ids }) =>
      this.invoices.fetch(ids).pipe(
        map((invoices) => FetchInvoicesComplete({ invoices })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  fetchInvoicesTotal$ = this.actions$.pipe(
    ofType(LoadInvoicesTotal),
    concatMap(({ invoiceIds }) =>
      this.invoices.totalBatch(invoiceIds).pipe(
        map((invoices) =>
          LoadInvoicesTotalComplete({
            changes: lodash.toPairs(invoices).map(([id, total]) => ({ id, changes: { invoiceTotal: total } })),
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addInvoice$ = this.actions$.pipe(
    ofType(AddInvoice),
    concatMap(({ invoice }) => {
      return this.invoices.add(this.invoices.newInvoicePayload(invoice)).pipe(
        tap(() => this.toaster.success($localize`Invoice created successfully.`)),
        map((invoice) => AddInvoiceComplete({ invoice })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  updateInvoice$ = this.actions$.pipe(
    ofType(UpdateInvoice),
    concatMap(({ invoice }) => {
      return this.invoices.update(invoice.id, this.invoices.editInvoicePayload(invoice)).pipe(
        tap(() => this.toaster.success($localize`Invoice updated successfully.`)),
        map((changes) => UpdateInvoiceComplete({ update: { id: invoice.id, changes } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  updateInvoiceStatus$ = this.actions$.pipe(
    ofType(UpdateInvoiceStatus),
    concatMap(({ invoiceId, status }) => {
      return this.invoices.status(invoiceId, status).pipe(
        tap(() => this.toaster.success($localize`Invoice Status updated successfully.`)),
        map((res) => UpdateInvoiceStatusComplete({ update: { id: invoiceId, changes: res } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  updateInvoiceAddress$ = this.actions$.pipe(
    ofType(UpdateInvoiceAddress),
    concatMap(({ id, addressKey }) => {
      return this.invoices.update(id, { address_key: addressKey }).pipe(
        tap(() => this.toaster.success($localize`Invoice Address updated successfully.`)),
        map((res) => UpdateInvoiceAddressComplete({ update: { id, changes: res } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  setInvoicePaymentGateway$ = this.actions$.pipe(
    ofType(SetInvoicePaymentGateway),
    concatMap(({ invoiceId, gatewayId }) => {
      return this.invoices.setGateway(invoiceId, gatewayId).pipe(
        tap(() => this.toaster.success($localize`Invoice gateway updated successfully.`)),
        map((res) =>
          SetInvoicePaymentGatewayComplete({
            update: {
              id: invoiceId,
              changes: { gatewayId: gatewayId, onlinePayment: 1 },
            },
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  enableInvoiceOnlinePayment$ = this.actions$.pipe(
    ofType(EnableInvoiceOnlinePayment),
    concatMap(({ invoiceId }) => {
      return this.invoices.enablePayment(invoiceId).pipe(
        tap(() => this.toaster.success($localize`Invoice online payment enabled successfully.`)),
        map((res) => EnableInvoiceOnlinePaymentComplete({ update: { id: invoiceId, changes: { onlinePayment: 1 } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  disableInvoiceOnlinePayment$ = this.actions$.pipe(
    ofType(DisableInvoiceOnlinePayment),
    concatMap(({ invoiceId }) => {
      return this.invoices.disablePayment(invoiceId).pipe(
        tap(() => this.toaster.success($localize`Invoice online payment disabled successfully.`)),
        map((res) => DisableInvoiceOnlinePaymentComplete({ update: { id: invoiceId, changes: { onlinePayment: 0 } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  scheduleInvoice$ = this.actions$.pipe(
    ofType(ScheduleInvoice),
    concatMap(({ invoiceId, date }) => {
      return this.invoices.schedule(invoiceId, date).pipe(
        tap(() => this.toaster.success(date ? 'Invoice scheduled successfully.' : 'Invoice schedule canceled.')),
        map((res) => ScheduleInvoiceComplete({ update: { id: invoiceId, changes: { scheduled: date } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  loadInvoice$ = this.actions$.pipe(
    ofType(LoadInvoice),
    concatMap(({ invoiceId }) =>
      this.invoices.get(invoiceId).pipe(
        map((invoice) => LoadInvoiceComplete({ invoice })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  refundInvoice$ = this.actions$.pipe(
    ofType(RefundInvoice),
    concatMap(({ invoiceId }) =>
      this.invoices.refund(invoiceId).pipe(
        tap(() => this.toaster.success($localize`Invoice refunded successfully.`)),
        map((invoiceResponse) => RefundInvoiceComplete({ update: { id: invoiceId, changes: invoiceResponse } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveInvoice$ = this.actions$.pipe(
    ofType(ArchiveInvoice),
    concatMap(({ invoice }) =>
      this.invoices.archive(invoice).pipe(
        tap(() => this.toaster.success($localize`Invoice archived successfully.`)),
        map((invoiceResponse) => ArchiveInvoiceComplete({ update: { id: invoice.id, changes: invoiceResponse } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unarchiveInvoice$ = this.actions$.pipe(
    ofType(UnArchiveInvoice),
    concatMap(({ invoice }) =>
      this.invoices.unarchive(invoice).pipe(
        tap(() => this.toaster.success($localize`Invoice unarchived successfully.`)),
        map((invoiceResponse) => UnArchiveInvoiceComplete({ update: { id: invoice.id, changes: invoiceResponse } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteInvoice$ = this.actions$.pipe(
    ofType(DeleteInvoice),
    concatMap(({ invoice }) =>
      this.invoices.delete(invoice).pipe(
        tap(() => this.toaster.success($localize`Invoice deleted successfully.`)),
        map(() => DeleteInvoiceComplete({ id: invoice.id })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  payWithSavedCard$ = this.actions$.pipe(
    ofType(PayInvoiceWithSavedCard),
    concatMap(({ invoiceId, total, description }) =>
      this.invoices.payWithSavedCard(invoiceId, total, description).pipe(
        tap(() => this.toaster.success($localize`Invoice charged successfully.`)),
        map((invoice) => PayInvoiceWithSavedCardComplete({ update: { id: invoiceId, changes: invoice } })),
        catchError((error) => {
          this.toaster.info(
            $localize`Please send the invoice to the guest so that they can re-enter their card details and submit the payment.`,
            $localize`Unable to charge invoice`
          )
          return of(ActionFailed({ error }))
        })
      )
    )
  )

  constructor(
    private actions$: Actions,
    private invoices: InvoiceService,
    private store: Store<any>,
    private toaster: Toaster
  ) {
    this.store
      .pipe(
        select(selectAllInvoices),
        map((invoices) => invoices.filter((t) => !t.invoiceTotal && !!t.id).map((t) => t.id)),
        map((ids) => lodash.sortBy(lodash.uniq(ids))),
        filter((ids) => ids.length > 0),
        distinctUntilChanged((a, b) => lodash.isEqual(a, b))
      )
      .subscribe((invoiceIds) => {
        this.store.dispatch(LoadInvoicesTotal({ invoiceIds }))
      })
  }
}
